import React from 'react';
import { Box, Text, Code, Link } from '@chakra-ui/react';
import LoadingDots from './LoadingDots';
import { Message } from '../types';

interface ChatMessageProps {
  message: Message;
}

const ChatMessage = ({ message }: ChatMessageProps): JSX.Element => {
  // Extract reusable values to improve readability
  const isUserMessage = message.type === 'user';

  // Helper functions to format different types of content
  const renderCodeBlock = (content: string, contentHash: string): JSX.Element => {
    const codeContent = content.slice(3, -3).trim();
    const firstLine = codeContent.split('\n')[0].trim();
    const code = codeContent
      .split('\n')
      .slice(1)
      .join('\n')
      .trim();

    return (
      <Box key={`code-${contentHash}`} my={2} bg="gray.50" p={3} borderRadius="md" overflowX="auto">
        {firstLine && (
          <Text fontSize="xs" color="gray.500" mb={1}>
            {firstLine}
          </Text>
        )}
        <Code p={2} borderRadius="md" whiteSpace="pre" display="block" bg="gray.50">
          {code}
        </Code>
      </Box>
    );
  };

  const renderInlineCode = (content: string, contentHash: string): JSX.Element => {
    const codeContent = content.slice(1, -1);
    return <Code key={`inline-${contentHash as string}`}>{codeContent}</Code>;
  };

  const renderBoldText = (content: string, contentHash: string): JSX.Element => {
    const boldContent = content.slice(2, -2);
    return (
      <Text key={`bold-${contentHash}`} as="strong" fontWeight="bold">
        {boldContent}
      </Text>
    );
  };

  const renderUrl = (url: string, contentHash: string): JSX.Element => {
    return (
      <Link
        key={`url-${contentHash}`}
        href={url}
        color="blue.400"
        textDecoration="underline"
        isExternal
        onClick={e => e.stopPropagation()}
        fontFamily="Lexend"
        fontWeight="light"
      >
        {url}
      </Link>
    );
  };

  // Create a unique content hash for keys
  const createContentHash = (text: string): string => {
    return text.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '');
  };

  // Process URLs within text
  const processUrls = (text: string): JSX.Element[] => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map(part => {
      const contentHash = createContentHash(part);

      if (part.match(urlRegex)) {
        return renderUrl(part, contentHash);
      }

      return <React.Fragment key={`text-${contentHash}`}>{part}</React.Fragment>;
    });
  };

  // Process bold text formatting
  const processBoldText = (text: string): JSX.Element[] => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);

    return parts.flatMap(part => {
      const contentHash = createContentHash(part);

      if (part.startsWith('**') && part.endsWith('**')) {
        return renderBoldText(part, contentHash);
      }

      return processUrls(part);
    });
  };

  // Process inline code formatting
  const processInlineCode = (text: string): JSX.Element[] => {
    const parts = text.split(/(`[^`]+`)/g);

    return parts.flatMap(part => {
      const contentHash = createContentHash(part);

      if (part.startsWith('`') && part.endsWith('`')) {
        return renderInlineCode(part, contentHash);
      }

      return processBoldText(part);
    });
  };

  // Main function to format text with markdown
  const renderFormattedText = (text: string): JSX.Element => {
    // Handle code blocks first
    const parts = text.split(/(```[\s\S]*?```)/g);

    return (
      <>
        {parts.map(part => {
          const contentHash = createContentHash(part);

          if (part.startsWith('```') && part.endsWith('```')) {
            return renderCodeBlock(part, contentHash);
          }

          return processInlineCode(part);
        })}
      </>
    );
  };

  // Message styling based on sender type
  const messageStyles = {
    bg: isUserMessage ? 'blue.400' : 'white',
    color: isUserMessage ? 'white' : 'gray.800',
    marginLeft: isUserMessage ? 'auto' : '0',
    borderColor: isUserMessage ? 'blue.400' : 'gray.200',
  };

  return (
    <Box w="full" display="flex">
      <Box
        maxW="80%"
        px={4}
        py={3}
        borderRadius="lg"
        fontSize="sm"
        fontFamily="Lexend"
        fontWeight="light"
        boxShadow="sm"
        border="1px solid"
        {...messageStyles}
      >
        <Text whiteSpace="pre-wrap" style={{ wordBreak: 'break-word' }}>
          {message.isLoading ? <LoadingDots /> : renderFormattedText(message.content)}
        </Text>
      </Box>
    </Box>
  );
};

export default ChatMessage;
